import React from 'react';

interface AncalagonLogoProps {
  height?: number | string;
  className?: string;
  fill?: string;
}

export const AncalagonLogo: React.FC<AncalagonLogoProps> = ({ height = 24, className = '', fill = 'currentColor' }) => {
  // Calculate width based on original aspect ratio (380/322)
  const width = typeof height === 'number' ? Math.round(height * (380 / 322)) : 'auto';

  // The logo is monochrome and defaults its fill to `currentColor` so it can follow the
  // theme. The `.ancalagon-logo` class (see src/app/app.css) sets `color` to the PF6 regular
  // text-colour token, which is dark on light themes and light on dark themes — so the logo
  // shows its "negative" light version in dark mode instead of vanishing against the dark
  // masthead. Consumers can still force a colour by passing an explicit `fill` prop.
  return (
    <svg
      height={height}
      width={width}
      viewBox="0 0 380 322"
      className={`ancalagon-logo ${className}`.trim()}
      aria-label="Ancalagon logo"
    >
      <title>Ancalagon logo</title>
      <defs>
        <linearGradient x1="68%" y1="2.25860997e-13%" x2="32%" y2="100%" id="linearGradient-basic-masthead">
          <stop stopColor="#2B9AF3" offset="0%"></stop>
          <stop stopColor="#73BCF7" stopOpacity="0.502212631" offset="100%"></stop>
        </linearGradient>
      </defs>

      <g transform="translate(0.000000,322.000000) scale(0.100000,-0.100000)" fill={fill} stroke="none">
        <path
          d="M3047 3150 c-56 -10 -202 -52 -428 -126 -191 -61 -261 -75 -326 -63
-73 14 -118 51 -75 62 10 3 5 5 -12 6 -20 1 -36 -8 -55 -30 -14 -18 -44 -48
-65 -68 -22 -20 -96 -88 -165 -153 -103 -94 -129 -114 -143 -107 -11 6 -18 21
-18 37 0 16 -5 33 -12 40 -8 8 -8 15 2 27 19 23 -6 19 -30 -5 -27 -27 -26 -31
16 -106 25 -45 35 -73 31 -92 -21 -121 -94 -416 -109 -446 -10 -18 -12 -16
-25 15 -8 19 -34 52 -57 74 -24 22 -73 87 -109 145 -36 58 -90 134 -121 168
-30 35 -62 78 -70 96 -14 30 -23 35 -133 66 -144 40 -184 59 -220 106 -15 20
-35 45 -43 55 -12 16 -37 21 -143 29 -145 12 -187 25 -297 94 -41 25 -76 42
-78 36 -2 -6 15 -44 38 -85 22 -42 38 -80 34 -86 -4 -7 -21 -9 -40 -6 -19 3
-34 4 -34 3 0 -2 50 -41 111 -87 61 -46 117 -89 125 -95 8 -7 17 -21 19 -33 6
-33 12 -38 83 -78 79 -44 218 -175 307 -288 82 -105 165 -233 164 -254 0 -24
-52 -124 -66 -129 -8 -2 -13 7 -13 25 l-1 28 -15 -27 c-8 -16 -19 -28 -23 -28
-5 0 -14 12 -19 28 l-9 27 -8 -37 c-4 -21 -11 -36 -15 -33 -5 3 -6 15 -3 28 4
20 3 20 -17 -5 -26 -33 -40 -36 -40 -8 0 28 -18 25 -26 -5 -6 -24 -34 -36 -34
-14 0 5 -6 7 -12 3 -7 -4 -4 3 7 16 l20 23 -32 -17 c-38 -19 -47 -20 -37 -5 4
8 -1 9 -16 4 -19 -6 -22 -4 -17 9 6 14 3 16 -13 11 -22 -7 -28 10 -7 18 6 3
-3 3 -22 0 -30 -5 -33 -4 -28 11 6 14 3 16 -15 11 -19 -7 -20 -5 -15 26 l7 34
-20 -23 c-25 -30 -43 -19 -38 24 5 42 0 43 -13 4 -14 -39 -27 -38 -44 3 l-14
32 -1 -27 c0 -16 -3 -28 -7 -28 -16 0 -31 16 -36 35 -4 18 -5 18 -6 -2 -1 -27
-31 -32 -31 -6 0 15 -2 14 -15 -3 -14 -19 -14 -19 -15 0 0 19 0 19 -21 0 -16
-14 -22 -15 -28 -5 -6 10 -10 9 -15 -4 -3 -9 -14 -15 -23 -13 -14 3 -17 12
-14 51 1 27 9 64 17 82 21 50 17 82 -13 131 -27 41 -51 60 -40 30 19 -49 19
-89 -1 -115 -26 -35 -57 -105 -72 -161 -15 -54 -35 -87 -43 -68 -3 7 -6 -1 -6
-18 -1 -46 -12 -48 -31 -6 -65 140 -73 153 -101 167 l-28 15 21 -36 c16 -27
20 -50 19 -97 -1 -34 3 -75 7 -92 5 -16 5 -43 1 -58 -4 -15 -3 -42 2 -59 5
-19 5 -36 0 -41 -5 -5 -7 -35 -5 -66 4 -55 4 -55 -10 -26 l-15 30 -1 -31 c0
-21 14 -51 40 -90 31 -44 40 -66 37 -88 -3 -23 3 -37 28 -63 18 -18 36 -33 41
-33 8 0 64 -90 64 -104 0 -3 -7 -6 -15 -6 -14 0 -14 -3 0 -30 22 -42 19 -57
-6 -34 -22 19 -22 19 -14 -6 19 -67 80 -127 153 -151 42 -14 51 -5 25 24 -15
15 -20 30 -16 50 6 30 18 35 27 12 3 -8 14 -15 24 -15 27 0 72 -38 88 -74 7
-17 13 -25 14 -17 0 8 -14 39 -31 69 -24 43 -28 57 -17 63 23 15 62 10 98 -12
l35 -21 -20 22 c-11 12 -37 31 -57 41 -58 30 -73 60 -72 148 1 81 19 130 74
201 45 58 48 67 17 47 -15 -9 -27 -16 -27 -14 0 2 13 27 28 56 33 59 29 61 90
-45 45 -77 121 -153 186 -186 42 -20 146 -115 146 -132 0 -18 55 -95 75 -104
19 -8 18 -10 -10 -25 -17 -8 -78 -52 -135 -98 -58 -45 -130 -93 -160 -106 -30
-14 -82 -47 -116 -73 -68 -55 -88 -64 -156 -74 -25 -4 -52 -13 -58 -21 -17
-21 -15 -156 2 -166 22 -14 29 -10 16 10 -10 16 -9 23 5 39 9 10 17 29 17 41
0 19 3 20 20 11 14 -8 26 -8 40 0 23 13 25 5 5 -30 -21 -37 -19 -103 4 -131
16 -19 22 -22 32 -11 11 10 10 12 0 12 -11 0 -11 3 -1 15 6 8 9 23 5 35 -8 24
21 110 36 110 14 0 10 -56 -7 -100 -14 -33 -14 -46 -5 -67 11 -25 13 -25 25
-7 8 10 17 38 20 61 4 23 21 61 37 85 85 125 117 163 137 166 20 3 22 0 16
-25 -4 -15 -11 -39 -16 -52 -15 -42 20 -4 48 51 39 77 60 58 44 -39 -13 -77
-12 -82 9 -56 10 12 27 53 37 90 10 37 23 71 28 75 13 9 24 -16 25 -56 1 -40
10 -28 19 24 8 48 36 75 52 50 8 -13 10 -13 10 1 0 11 15 20 47 28 27 6 70 19
96 27 27 9 50 14 51 12 1 -2 26 -28 54 -59 l52 -56 -20 -46 c-11 -25 -34 -92
-51 -147 -16 -56 -47 -137 -68 -179 -22 -42 -42 -99 -46 -126 -12 -85 -63
-121 -155 -112 -59 7 -100 -6 -108 -33 -4 -12 -2 -13 9 -2 7 6 22 12 33 11 20
0 20 -1 -2 -16 -26 -19 -30 -38 -3 -22 14 10 23 9 42 -4 13 -10 43 -17 66 -17
32 0 43 -4 43 -15 0 -8 7 -15 16 -15 8 0 12 5 9 10 -3 6 4 10 15 10 12 0 35 9
50 20 16 11 42 23 59 26 17 4 31 9 31 13 1 23 18 31 44 21 24 -9 27 -14 22
-36 -5 -19 -3 -25 6 -22 14 5 15 53 3 102 -7 27 -6 28 14 17 12 -7 19 -16 16
-22 -9 -13 9 -11 23 3 9 9 15 9 24 0 17 -17 49 -15 64 6 13 16 15 16 29 -2 15
-20 15 -20 30 0 8 10 15 27 15 37 0 19 74 92 93 92 5 0 16 -16 22 -35 15 -42
26 -25 22 37 -1 23 3 43 8 45 6 2 26 -21 45 -50 28 -44 36 -50 38 -33 2 12 -6
46 -17 76 -19 51 -20 56 -4 62 10 4 23 -2 35 -16 19 -25 25 -12 8 20 -8 14 -7
25 5 43 13 19 14 29 5 46 -9 17 -7 26 10 50 23 33 17 38 -17 15 -28 -20 -36
-1 -16 38 13 25 12 28 -3 25 -23 -4 -27 13 -9 36 14 19 17 17 72 -35 32 -30
73 -61 90 -68 24 -10 35 -23 43 -50 17 -63 11 -90 -41 -186 -56 -106 -101
-151 -156 -160 -99 -17 -162 -60 -147 -99 6 -15 8 -14 18 5 14 28 39 38 30 12
-5 -14 -4 -15 5 -7 22 20 58 -5 53 -35 -4 -25 -4 -25 11 -6 14 18 17 18 42 5
24 -13 30 -12 69 10 48 26 71 20 62 -16 -5 -17 -2 -24 9 -24 8 0 15 5 15 10 0
6 7 10 16 10 29 0 92 59 100 93 3 17 11 43 15 56 9 22 10 23 35 6 35 -23 42
-11 16 25 -38 51 -24 69 34 45 20 -8 39 -13 42 -10 3 3 -11 23 -32 44 -39 40
-36 59 7 45 21 -6 21 -6 6 12 -19 22 -31 87 -15 77 6 -4 35 -22 65 -41 84 -54
248 -129 331 -152 41 -11 149 -30 240 -41 361 -46 695 10 822 137 20 20 40 49
43 64 9 41 -13 104 -52 150 -37 43 -90 64 -193 75 -68 7 -112 15 -125 23 -5 4
-18 8 -27 9 -34 5 -194 76 -243 108 -88 56 -112 100 -125 229 -19 186 52 302
195 321 64 8 182 42 190 55 4 6 -12 7 -42 3 l-48 -6 24 26 c13 15 39 31 57 37
35 12 43 25 22 34 -8 3 2 18 27 40 29 26 35 36 23 36 -10 0 -18 1 -18 3 1 1 9
38 20 82 10 44 16 82 13 85 -3 3 -8 0 -10 -7 -8 -23 -85 -138 -112 -167 -42
-45 -102 -80 -112 -65 -5 9 -20 3 -56 -26 -26 -21 -56 -47 -66 -57 -15 -15
-22 -17 -33 -7 -12 10 -13 9 -8 -6 7 -16 3 -17 -27 -12 -19 3 -28 3 -21 0 23
-9 12 -23 -20 -24 -18 0 -27 -3 -20 -6 23 -9 12 -23 -20 -23 -30 -1 -31 -2
-15 -14 20 -15 14 -23 -31 -36 -24 -8 -25 -8 -4 -9 27 -1 28 -9 5 -28 -10 -8
-12 -11 -4 -7 11 6 13 3 7 -12 -5 -10 -10 -28 -13 -39 -3 -11 -7 -27 -10 -35
-4 -8 -5 -19 -4 -24 1 -4 -2 -13 -6 -20 -5 -6 -8 -22 -7 -36 7 -102 19 -181
34 -224 29 -85 31 -80 -33 -90 -53 -9 -60 -7 -79 12 -19 18 -20 24 -8 52 7 17
20 35 29 41 12 7 8 8 -16 4 -53 -10 -92 -9 -108 5 -23 19 -18 61 13 93 l27 30
-68 -6 c-64 -6 -68 -5 -84 19 -22 34 -9 71 35 101 l33 22 -68 -5 -68 -6 -28
36 c-26 34 -27 37 -12 66 8 16 27 33 41 36 49 13 15 15 -45 3 -60 -11 -60 -11
-101 21 -22 17 -40 36 -40 41 0 6 11 21 25 35 23 23 23 25 5 19 -24 -8 -24 -7
-4 40 19 48 44 226 44 328 0 46 6 92 15 113 42 101 143 193 298 270 115 58
194 76 319 72 54 -2 98 -2 98 0 0 2 -35 42 -77 88 -92 100 -104 125 -76 163
44 61 156 91 298 81 44 -4 82 -4 85 -2 8 7 -50 49 -138 99 -173 99 -210 143
-219 256 -8 100 24 150 135 206 69 35 44 48 -61 31z m-2397 -540 c0 -5 -2 -10
-4 -10 -3 0 -8 5 -11 10 -3 6 -1 10 4 10 6 0 11 -4 11 -10z m542 -697 c-10
-26 -10 -24 -11 22 0 42 2 46 11 28 7 -15 7 -30 0 -50z m-632 -238 c10 -12 8
-16 -15 -25 -14 -5 -33 -10 -41 -10 -20 0 -17 38 4 43 31 8 39 7 52 -8z m2383
-927 l32 -31 -29 -10 c-53 -18 -99 26 -64 61 18 18 26 15 61 -20z m77 -71 c0
-16 -35 -1 -38 16 -3 16 -1 17 17 5 12 -7 21 -17 21 -21z m-2520 13 c-20 -13
-33 -13 -25 0 3 6 14 10 23 10 15 0 15 -2 2 -10z m1148 -32 c36 -78 36 -81 -7
-136 -49 -61 -59 -68 -68 -45 -3 10 -15 30 -25 45 -20 29 -22 37 -9 57 8 13
12 13 27 0 16 -13 16 -12 5 9 -10 19 -8 29 15 67 14 25 29 45 34 45 4 0 17
-19 28 -42z m-78 -209 c0 -3 -8 -13 -18 -23 -18 -15 -20 -14 -37 23 -10 23
-15 48 -10 58 6 16 10 14 36 -17 16 -19 29 -37 29 -41z m-88 -36 c23 -21 23
-30 -2 -43 -17 -9 -22 -7 -30 15 -16 43 0 57 32 28z m-67 -54 c-10 -15 -25
-10 -19 6 4 8 10 12 15 9 5 -3 7 -10 4 -15z m445 -213 c0 -2 -7 -7 -16 -10 -8
-3 -12 -2 -9 4 6 10 25 14 25 6z"
        />
        <path d="M2898 1393 c6 -2 18 -2 25 0 6 3 1 5 -13 5 -14 0 -19 -2 -12 -5z" />
        <path d="M2858 1343 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
      </g>
    </svg>
  );
};
